import { useState } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldCheck, Upload, Loader2, AlertTriangle, LogOut, CheckCircle2, IdCard } from 'lucide-react';

/**
 * First-time KYC form for students. Blocks the portal until submitted.
 * - Fields: full_name, dob, gender, address, state, pincode, id_type (aadhaar|pan), id_number, photo, phone, parent_guardian
 * - Photo upload → returns storage_path, then submit KYC via /api/students/kyc/submit
 * - If rejected, shows the reason and lets student re-submit.
 */
export default function StudentKycForm({ me, onSubmitted, onLogout }) {
  const rejected = me?.kyc_status === 'rejected';
  const [form, setForm] = useState({
    full_name: me?.name || '',
    date_of_birth: me?.date_of_birth || '',
    gender: '',
    address: me?.address || '',
    state: 'West Bengal',
    pincode: '',
    id_type: 'aadhaar',
    id_number: '',
    parent_guardian: '',
    phone: me?.phone || '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPath, setPhotoPath] = useState('');
  const [photoPreview, setPhotoPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo too large (max 5MB)');
      return;
    }
    setPhotoFile(file);
    setPhotoPath('');
    setPhotoPreview(URL.createObjectURL(file));
  };

  const uploadPhoto = async () => {
    if (!photoFile) return null;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', photoFile);
      const { data } = await api.post('/students/kyc/upload-photo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotoPath(data.storage_path);
      return data.storage_path;
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Photo upload failed');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const validate = () => {
    if (!form.full_name.trim()) return 'Full name is required';
    if (!form.date_of_birth) return 'Date of birth is required';
    if (!form.address.trim()) return 'Address is required';
    if (!form.state.trim()) return 'State is required';
    if (!form.id_number.trim()) return 'ID number is required';
    if (form.id_type === 'aadhaar' && !/^\d{12}$/.test(form.id_number.replace(/\s/g, ''))) return 'Aadhaar must be 12 digits';
    if (form.id_type === 'pan' && !/^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(form.id_number.replace(/\s/g, ''))) return 'Invalid PAN format (e.g. ABCDE1234F)';
    if (!photoFile && !photoPath) return 'Please upload a passport-style photo';
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      let path = photoPath;
      if (!path && photoFile) path = await uploadPhoto();
      if (!path) return;
      const payload = { ...form, photo_storage_path: path, id_number: form.id_number.replace(/\s/g, '').toUpperCase() };
      const { data } = await api.post('/students/kyc/submit', payload);
      toast.success(data.message || 'KYC submitted. Awaiting admin verification.');
      onSubmitted?.();
    } catch (e2) {
      toast.error(formatError(e2.response?.data?.detail) || 'KYC submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg py-8 px-4" data-testid="student-kyc-form">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-brand-primary/10 flex items-center justify-center">
              <ShieldCheck size={24} className="text-brand-primary" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-brand-text-primary">Complete Your KYC</h1>
              <p className="text-sm text-brand-text-secondary">One-time verification to activate your Quantora Academy account</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onLogout} data-testid="kyc-logout-btn" className="text-xs">
            <LogOut size={14} className="mr-1" /> Logout
          </Button>
        </div>

        {rejected && me?.kyc_rejected_reason && (
          <Card className="border-brand-error/40 bg-brand-error/5 mb-4" data-testid="kyc-rejected-banner">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle size={20} className="text-brand-error shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-brand-error">Your previous KYC was rejected</p>
                <p className="text-sm text-brand-text-primary mt-0.5">{me.kyc_rejected_reason}</p>
                <p className="text-xs text-brand-text-secondary mt-1">Please correct the highlighted issue and re-submit below.</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-brand-border bg-white">
          <CardHeader className="pb-3 border-b border-brand-border">
            <CardTitle className="text-base flex items-center gap-2"><IdCard size={16} /> Personal Details</CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-5">
            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Full Legal Name *</Label>
                <Input value={form.full_name} onChange={e => set({ full_name: e.target.value })} data-testid="kyc-name" className="h-10" />
              </div>
              <div>
                <Label className="text-xs">Date of Birth *</Label>
                <Input type="date" value={form.date_of_birth} onChange={e => set({ date_of_birth: e.target.value })} data-testid="kyc-dob" className="h-10" />
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Gender</Label>
                <Select value={form.gender} onValueChange={v => set({ gender: v })}>
                  <SelectTrigger className="h-10" data-testid="kyc-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Phone Number</Label>
                <Input value={form.phone} onChange={e => set({ phone: e.target.value })} data-testid="kyc-phone" className="h-10" placeholder="+91 …" />
              </div>
              <div>
                <Label className="text-xs">Parent / Guardian Name (optional)</Label>
                <Input value={form.parent_guardian} onChange={e => set({ parent_guardian: e.target.value })} data-testid="kyc-guardian" className="h-10" />
              </div>
            </div>

            {/* Address */}
            <div>
              <Label className="text-xs">Address *</Label>
              <Textarea value={form.address} onChange={e => set({ address: e.target.value })} rows={2} data-testid="kyc-address" placeholder="House/Street/Village, District" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">State *</Label>
                <Input value={form.state} onChange={e => set({ state: e.target.value })} data-testid="kyc-state" className="h-10" />
              </div>
              <div>
                <Label className="text-xs">PIN Code</Label>
                <Input value={form.pincode} onChange={e => set({ pincode: e.target.value })} data-testid="kyc-pincode" className="h-10" placeholder="6 digits" />
              </div>
            </div>

            {/* Photo ID */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Photo ID Type *</Label>
                <Select value={form.id_type} onValueChange={v => set({ id_type: v })}>
                  <SelectTrigger className="h-10" data-testid="kyc-id-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aadhaar">Aadhaar</SelectItem>
                    <SelectItem value="pan">PAN Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">{form.id_type === 'aadhaar' ? 'Aadhaar Number (12 digits) *' : 'PAN Number (e.g. ABCDE1234F) *'}</Label>
                <Input
                  value={form.id_number}
                  onChange={e => set({ id_number: e.target.value.toUpperCase() })}
                  data-testid="kyc-id-number"
                  className="h-10 font-mono uppercase tracking-wider"
                  placeholder={form.id_type === 'aadhaar' ? '1234 5678 9012' : 'ABCDE1234F'}
                  maxLength={form.id_type === 'aadhaar' ? 14 : 10}
                />
              </div>
            </div>

            {/* Passport Photo */}
            <div>
              <Label className="text-xs">Passport-style Photo * <span className="text-brand-text-secondary font-normal">(JPG/PNG · max 5MB)</span></Label>
              <div className="mt-2 flex items-start gap-4">
                {photoPreview ? (
                  <img src={photoPreview} alt="preview" className="w-28 h-28 object-cover rounded-lg border border-brand-border" />
                ) : (
                  <div className="w-28 h-28 rounded-lg border-2 border-dashed border-brand-border flex items-center justify-center text-brand-text-secondary">
                    <Upload size={22} />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoChange}
                    data-testid="kyc-photo-input"
                    className="text-sm block w-full border border-brand-border rounded-lg px-3 py-2 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-brand-primary file:text-white file:text-xs file:cursor-pointer"
                  />
                  <p className="text-[11px] text-brand-text-secondary mt-1.5">A clear front-facing photo. This will appear on your ID card.</p>
                  {photoPath && <p className="text-[11px] text-brand-success mt-1 flex items-center gap-1"><CheckCircle2 size={11} /> Photo uploaded</p>}
                </div>
              </div>
            </div>

            <div className="pt-3 border-t border-brand-border flex items-center justify-between gap-3">
              <p className="text-[11px] text-brand-text-secondary">By submitting, you confirm the information above is accurate. Providing false information may lead to account termination.</p>
              <Button
                onClick={handleSubmit}
                disabled={submitting || uploading}
                className="bg-brand-primary text-white h-10 px-6 shrink-0"
                data-testid="kyc-submit-btn"
              >
                {(submitting || uploading) ? <Loader2 size={16} className="animate-spin mr-2" /> : <ShieldCheck size={16} className="mr-2" />}
                {rejected ? 'Re-submit KYC' : 'Submit KYC'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
